import jwt_decode from 'jwt-decode';
import userDataDB from './userDataDB'; // for background sync
import setJWTTokenInIndexedDB from './setJWTTokenInIndexedDB';
import removeAuthInfoFromOfflineDB from '../pages/Login/utils/removeAuthInfoFromOflineDB';

export const ACCESS_TOKEN_STORAGE_KEY = 'auth_tkn';

/**
 * Fired on this window whenever the access token is stored or dropped.
 *
 * localStorage is not reactive, so anything whose behaviour depends on "is there a
 * session" has no way to notice one starting or ending. Most entry points navigate
 * with `window.location`, which remounts the app and hides the problem; signup uses
 * `history.push`, which does not. This event is what the two writers below have in
 * common, so a listener on it sees every transition regardless of the route change.
 */
export const SESSION_CHANGE_EVENT = 'zelp:session-change';

const notifySessionChange = () => {
    try {
        window.dispatchEvent(new Event(SESSION_CHANGE_EVENT));
    }
    catch (err) {
        // No window (tests, the service worker bundle). Nothing is listening there.
    }
};

/**
 * How long before its `exp` an access token stops being considered usable.
 *
 * Access tokens live 15 minutes now, so a token that expires while a request is
 * in flight is a realistic event rather than a once-a-year one. Renewing a minute
 * early costs one extra call to /auth/refresh per session and removes the whole
 * class of "expired between the header being built and the server reading it"
 * failures, including the clock skew between the device and the API.
 */
export const EXPIRY_SKEW_SECONDS = 60;

/**
 * The access token lives in three places, and all three are load-bearing:
 *
 *  - localStorage, read by the app itself (Apollo, the REST requester).
 *  - the `userData` IndexedDB, read by the service worker's background sync.
 *  - the `notifications` IndexedDB, read by the service worker's push handler.
 *
 * localStorage is unreachable from a service worker, which is why the copies
 * exist. Before the token was short-lived they could be written once at login and
 * forgotten; now every refresh has to reach all three or the service worker keeps
 * presenting a token that expired fifteen minutes into the session. That is the
 * reason this module exists: so there is exactly one place that knows the list.
 */
export const getAccessToken = () => {
    try {
        return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) || null;
    }
    catch (err) {
        return null;
    }
};

export const setAccessToken = async (token) => {
    try {
        localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
    }
    catch (err) {
        // Storage disabled (Safari private browsing, quota). The token still
        // reaches the two IndexedDB copies below and the in-flight request that
        // asked for it, so the session works until the tab is closed.
    }

    // Both copies are best-effort: a browser that refuses IndexedDB should lose
    // background sync and push, not the ability to log in.
    try { await userDataDB.setJWT(token); }
    catch (err) { /* background sync will re-read on the next refresh */ }

    try { await setJWTTokenInIndexedDB(token); }
    catch (err) { /* push auth will re-read on the next refresh */ }

    notifySessionChange();
};

export const clearAccessToken = () => {
    try {
        localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    }
    catch (err) {
        // Nothing to remove if storage is unavailable.
    }

    userDataDB.removeJWT().catch(() => { /* nothing stored */ });
    removeAuthInfoFromOfflineDB();

    notifySessionChange();
};

/**
 * Milliseconds since the epoch at which `token` expires, or null when the token
 * is absent, malformed, or carries no `exp`.
 */
export const getTokenExpiryMs = (token) => {
    if (!token) {
        return null;
    }

    try {
        const { exp } = jwt_decode(token);
        return typeof exp === 'number' ? exp * 1000 : null;
    }
    catch (err) {
        // Not a JWT we can read — treated as unusable by every caller below.
        return null;
    }
};

/**
 * Whether `token` can still be sent. A token we cannot decode counts as unusable:
 * the alternative is sending it and taking a 401, and a refresh attempt is both
 * cheaper and the same thing the 401 would trigger.
 */
export const isTokenUsable = (token, skewSeconds = EXPIRY_SKEW_SECONDS) => {
    const expiresAtMs = getTokenExpiryMs(token);

    if (expiresAtMs === null) {
        return false;
    }

    return Date.now() < expiresAtMs - (skewSeconds * 1000);
};
